import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query'
import { 
  dashboardApi, 
  ridesApi, 
  usersApi, 
  financeApi, 
  claimsApi, 
  rolesApi, 
  permissionsApi,
  zonesApi,
  subscriptionsApi,
  promoApi,
  notificationsApi,
  vehicleTypesApi,
  type ProcessPayoutPayload
} from '../services/api'
import { mapFinanceOverview } from '../lib/adminMappers'

// ═══════════════════════════════════════════════════════════════════════════
// QUERY KEYS - Centralisés pour une gestion cohérente du cache
// ═══════════════════════════════════════════════════════════════════════════

export const queryKeys = {
  // Dashboard
  dashboardStats: ['dashboard-stats'] as const,
  dashboardRides: (params?: object) => ['dashboard-rides', params] as const,
  
  // Users
  users: (params?: object) => ['users', params] as const,
  user: (id: string | number) => ['user', id] as const,
  
  // Drivers
  drivers: (params?: object) => ['drivers', params] as const,
  driver: (id: string | number) => ['driver', id] as const,
  
  // Rides
  rides: (params?: object) => ['rides', params] as const,
  ride: (id: string | number) => ['ride', id] as const,
  
  // Finance
  financeOverview: ['finance-overview'] as const,
  transactions: (params?: object) => ['transactions', params] as const,
  payouts: ['payouts'] as const,
  
  // Claims
  claims: (params?: object) => ['claims', params] as const,
  claim: (id: string | number) => ['claim', id] as const,
  
  // Roles & Permissions
  roles: (params?: object) => ['roles', params] as const,
  role: (id: number) => ['role', id] as const,
  permissions: ['permissions'] as const,
  permissionsGrouped: ['permissions-grouped'] as const,
  permissionGroups: ['permission-groups'] as const,
  
  // Zones
  zones: (params?: object) => ['zones', params] as const,
  zone: (id: string | number) => ['zone', id] as const,

  // Types de véhicules (catalogue dynamique)
  vehicleTypes: (params?: object) => ['vehicle-types', params] as const,
  
  // Subscriptions
  subscriptionPlans: ['subscription-plans'] as const,
  subscriptions: (params?: object) => ['subscriptions', params] as const,
  subscriptionStats: ['subscription-stats'] as const,
  
  // Promos
  promos: ['promos'] as const,
  
  // Notifications
  notifications: (params?: object) => ['notifications', params] as const,
} as const

// ═══════════════════════════════════════════════════════════════════════════
// STALE TIMES - Durées de fraîcheur des données par type
// ═══════════════════════════════════════════════════════════════════════════

export const staleTimes = {
  realtime: 5 * 1000,         // 5s - Données temps réel (courses actives)
  frequent: 30 * 1000,        // 30s - Données fréquemment mises à jour
  standard: 2 * 60 * 1000,    // 2min - Données standard
  stable: 5 * 60 * 1000,      // 5min - Données relativement stables
  static: 30 * 60 * 1000,     // 30min - Données rarement modifiées
} as const

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useDashboardStats(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: async () => {
      const { data } = await dashboardApi.getStats()
      return data.data
    },
    staleTime: staleTimes.frequent,
    ...options,
  })
}

export function useDashboardRides(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.dashboardRides(params),
    queryFn: async () => {
      const { data } = await dashboardApi.getRides(params)
      return data.data
    },
    staleTime: staleTimes.realtime,
    ...options,
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// USERS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useUsers(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.users(params),
    queryFn: async () => {
      const { data } = await usersApi.list(params)
      return data.data
    },
    staleTime: staleTimes.standard,
    ...options,
  })
}

export function useUser(id: string | number, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.user(id),
    queryFn: async () => {
      const { data } = await usersApi.show(id)
      return data.data
    },
    staleTime: staleTimes.standard,
    enabled: !!id,
    ...options,
  })
}

export function useSuspendUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, reason }: { id: string | number; reason: string }) => 
      usersApi.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useActivateUser() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: string | number) => usersApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// RIDES HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useRides(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.rides(params),
    queryFn: async () => {
      const { data } = await ridesApi.list(params)
      return data.data
    },
    staleTime: staleTimes.realtime,
    ...options,
  })
}

export function useRide(id: string | number, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.ride(id),
    queryFn: async () => {
      const { data } = await ridesApi.show(id)
      return data.data
    },
    staleTime: staleTimes.realtime,
    enabled: !!id,
    ...options,
  })
}

export function useForceCancelRide() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, reason }: { id: string | number; reason: string }) => 
      ridesApi.forceCancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rides'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-rides'] })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// FINANCE HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useFinanceOverview(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.financeOverview,
    queryFn: async () => {
      const { data } = await financeApi.overview()
      return mapFinanceOverview(data?.data ?? data)
    },
    staleTime: staleTimes.frequent,
    ...options,
  })
}

export function useTransactions(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.transactions(params),
    queryFn: async () => {
      const { data } = await financeApi.transactions(params)
      return data.data
    },
    staleTime: staleTimes.standard,
    ...options,
  })
}

export function usePayouts(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.payouts,
    queryFn: async () => {
      const { data } = await financeApi.payouts()
      return data.data
    },
    staleTime: staleTimes.standard,
    ...options,
  })
}

export function useProcessPayout() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: ProcessPayoutPayload) => financeApi.processPayout(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.payouts })
      queryClient.invalidateQueries({ queryKey: queryKeys.financeOverview })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// CLAIMS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useClaims(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.claims(params),
    queryFn: async () => {
      const { data } = await claimsApi.list(params)
      return data.data
    },
    staleTime: staleTimes.standard,
    ...options,
  })
}

export function useClaim(id: string | number, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.claim(id),
    queryFn: async () => {
      const { data } = await claimsApi.show(id)
      return data.data
    },
    staleTime: staleTimes.standard,
    enabled: !!id,
    ...options,
  })
}

export function useResolveClaim() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, status, resolution, admin_notes, refund_amount }:
      { id: string | number; status: 'resolved' | 'rejected'; resolution?: string; admin_notes?: string; refund_amount?: number }) =>
      claimsApi.resolve(id, { status, resolution, admin_notes, refund_amount }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claims'] })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// ROLES HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useRoles(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.roles(params),
    queryFn: async () => {
      const { data } = await rolesApi.list(params)
      return data.data
    },
    staleTime: staleTimes.stable,
    ...options,
  })
}

export function useRole(id: number, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.role(id),
    queryFn: async () => {
      const { data } = await rolesApi.show(id)
      return data.data
    },
    staleTime: staleTimes.stable,
    enabled: !!id,
    ...options,
  })
}

export function useCreateRole() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

export function useUpdateRole() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      rolesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

export function useDeleteRole() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: rolesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// PERMISSIONS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function usePermissions(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.permissions,
    queryFn: async () => {
      const { data } = await permissionsApi.list()
      return data.data
    },
    staleTime: staleTimes.static,
    ...options,
  })
}

export function usePermissionsGrouped(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.permissionsGrouped,
    queryFn: async () => {
      const { data } = await permissionsApi.grouped()
      return data.data
    },
    staleTime: staleTimes.static,
    ...options,
  })
}

export function usePermissionGroups(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.permissionGroups,
    queryFn: async () => {
      const { data } = await permissionsApi.groups()
      return data.data
    },
    staleTime: staleTimes.static,
    ...options,
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// ZONES HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useZones(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.zones(params),
    queryFn: async () => {
      const { data } = await zonesApi.list(params)
      return data.data
    },
    staleTime: staleTimes.stable,
    ...options,
  })
}

export function useZone(id: string | number, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.zone(id),
    queryFn: async () => {
      const { data } = await zonesApi.show(id)
      return data.data
    },
    staleTime: staleTimes.stable,
    enabled: !!id,
    ...options,
  })
}

export function useCreateZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: zonesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] })
    },
  })
}

export function useUpdateZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      zonesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] })
    },
  })
}

export function useDeleteZone() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: zonesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// SUBSCRIPTIONS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useSubscriptionPlans(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.subscriptionPlans,
    queryFn: async () => {
      const { data } = await subscriptionsApi.plans()
      return data.data
    },
    staleTime: staleTimes.stable,
    ...options,
  })
}

export function useSubscriptions(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.subscriptions(params),
    queryFn: async () => {
      const { data } = await subscriptionsApi.list(params)
      return data.data
    },
    staleTime: staleTimes.standard,
    ...options,
  })
}

export function useSubscriptionStats(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.subscriptionStats,
    queryFn: async () => {
      const { data } = await subscriptionsApi.stats()
      return data.data
    },
    staleTime: staleTimes.frequent,
    ...options,
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// PROMOS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function usePromos(options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.promos,
    queryFn: async () => {
      const { data } = await promoApi.list()
      return data.data
    },
    staleTime: staleTimes.stable,
    ...options,
  })
}

export function useCreatePromo() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: promoApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promos })
    },
  })
}

export function useUpdatePromo() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      promoApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promos })
    },
  })
}

export function useTogglePromo() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: promoApi.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promos })
    },
  })
}

export function useDeletePromo() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: promoApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.promos })
    },
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export function useNotifications(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.notifications(params),
    queryFn: async () => {
      const { data } = await notificationsApi.list(params)
      return data.data
    },
    staleTime: staleTimes.frequent,
    ...options,
  })
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES DE VÉHICULES HOOKS (catalogue dynamique)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Catalogue des types de véhicules (`GET /admin/vehicle-types`).
 *
 * Non paginé côté backend : c'est par nature une courte liste. Le cache est
 * « static » car un type ne change qu'à la main depuis le back-office, et
 * chaque mutation invalide explicitement la clé — les écrans qui consomment le
 * catalogue (formulaire chauffeur, tarifs) sont donc rafraîchis immédiatement.
 */
export function useVehicleTypes(params?: object, options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: queryKeys.vehicleTypes(params),
    queryFn: async () => {
      const { data } = await vehicleTypesApi.list(params)
      return data.data ?? []
    },
    staleTime: staleTimes.static,
    ...options,
  })
}

export function useCreateVehicleType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: any) => vehicleTypesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] })
    },
  })
}

export function useUpdateVehicleType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) =>
      vehicleTypesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] })
    },
  })
}

export function useToggleVehicleType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: vehicleTypesApi.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] })
    },
  })
}

export function useDeleteVehicleType() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: vehicleTypesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-types'] })
    },
  })
}
